import type { Collection, Poem } from '@maria-cavalheiro-website/shared-types'

/**
 * The complete works — twenty-three poems by Maria Cavalheiro, transcribed from
 * the author's own two collections.
 *
 * TRANSCRIPTION DISCIPLINE: verbatim. Non-standard spellings (`cascátas`, `briza`,
 * `Tão linhas como as flores`, `Uma dia estava chegando`) are the author's text and
 * are preserved exactly. Suspected typos are recorded in
 * `.ai/design-reference/author-queries.md` for the author to rule on; none were
 * silently corrected. The single normalisation carried over from the design is
 * `estå` -> `está` in `Romeu`, which repairs a character-encoding artefact rather
 * than editing a word.
 *
 * Order is the author's own: collection I first, then collection II, each in the
 * sequence of its index page. That order drives prev/next on the poem route.
 */
const SOURCE_POEMS: readonly Poem[] = [
  {
    slug: 'pombas-no-telhado',
    title: 'Pombas no telhado',
    publishedAt: '1984-01',
    collection: 1,
    theme: 'Pássaros',
    icon: 'bird',
    scene: 'pombas',
    stanzas: [
      [
        'Elas são simples pombas,',
        'Que estão ali no telhado,',
        'De anjos parecem sombras,',
        'Voando no céu azulado.',
      ],
      [
        'Suas asas, parecem leques,',
        'Voam com muito vigor!',
        'Pombas mansas, belas e leves',
        'Vivem com muito amor.',
      ],
      [
        'Pombas brancas e de outras cores,',
        'Sempre são muito graciosas…',
        'Tão linhas como as flores,',
        'Delicadas, meigas e carinhosas.',
      ],
      [
        'Elas saem sobre as casas,',
        'Em bandos a revoar.',
        'E a batida das suas asas.',
        'De longe se ouve a ecoar.',
      ],
      [
        'Fizeram um lindo ninho,',
        'Em frente das minhas janelas…',
        'Juntaram, com muito jeitinho',
        'Raminhos e palhinhas singelas.',
      ],
      [
        'Ali colocaram os ovinhos,',
        'Depois foram voar na praça,',
        'Logo nascem os pombinhos,',
        'Com alegria e muita graça.',
      ],
    ],
  },
  {
    slug: 'o-palhaco',
    title: 'O palhaço',
    publishedAt: '1984-03',
    collection: 1,
    theme: 'Memória',
    icon: 'smile',
    scene: 'palhaco',
    stanzas: [
      [
        'O palhaço canta e dança,',
        'De brincar ele não cansa,',
        'Esquece até uma desilusão,',
        'Brinca, com o chapéu na mão…',
        'Ajeita a calça listrada,',
        'Sorri com a cara pintada.',
      ],
      [
        'Ele é bom e inteligente',
        'Brinca e faz rir toda a gente',
        'E os que querem se divertir,',
        'Chegam perto para sentir.',
        'A alegria da criançada',
        'Que foram ver sua palhaçada.',
      ],
      [
        'Ele leva no coração,',
        'Uma boa recordação.',
        'Para o sul ou para o norte',
        'Vai animado no transporte',
        'Para longe, para onde for',
        'Sempre alegre com amor…',
      ],
      [
        'O palhaço é bom camarada…',
        'Nos faz rir da sua mancada,',
        'Nos faz rir do seu sapatão',
        'Quando se enrosca e se joga no chão,',
        'Parece um grande atleta,',
        'Fica dançando e alegrando a festa.',
      ],
    ],
  },
  {
    slug: 'andorinhas',
    title: 'Andorinhas',
    publishedAt: '1985',
    collection: 1,
    theme: 'Pássaros',
    icon: 'wind',
    scene: 'andorinhas',
    stanzas: [
      [
        'Quem ainda não viu?',
        'Bandos velozes, se envolvendo no ar',
        'Enchendo tudo, com seu piu,',
        'Muito insistente a gracejar.',
      ],
      [
        'Seu olhar, está na distância',
        'Espreitando intrusos e perigo.',
        'Não abandonam, com firme constância,',
        'De enfrentar o gavião, o pior inimigo.',
      ],
      [
        'No alto da torre voam…',
        'Com grito e ânimo persistentes',
        'Nas alturas, seus cantos ecoam,',
        'Enfrentando outros concorrentes.',
      ],
      [
        'Como sempre, há séria disputa',
        'Com outros emplumados, passarinhos,',
        'Mostram sua bravura, na luta,',
        'Defendendo seus lares e ninhos.',
      ],
      [
        'Andorinhas, tão pequeninas',
        'São um enfeite, do norte ao sul,',
        'Tão altivas, são como heroínas,',
        'Parecem pérolas no céu azul.',
      ],
      [
        'De longe elas vêm voando…',
        'Atravessam, montanhas e até o mar,',
        'Com grande alegria vão se espalhando,',
        'Procurando abrigo, em todo lugar,',
        'Porque a noite está chegando.',
        'E num doce adormecer vão descansar.',
      ],
    ],
  },
  {
    slug: 'a-janela',
    title: 'A janela',
    publishedAt: '1995-04-21',
    collection: 1,
    theme: 'Casa',
    icon: 'janela',
    scene: 'janela',
    stanzas: [
      [
        'Através da janela que dá para o jardim,',
        'Eu vejo o pé de alecrim',
        'Que está todo florido…',
        'Parece que está sorrindo.',
        'Perto das flores amarelas',
        'Tão delicadas e tão belas.',
      ],
      [
        'Vejo as borboletas a passear,',
        'Pelas flores a pousar,',
        'Quanta alegria, quanta emoção…',
        'Brotinhos nascendo pelo chão.',
        'Muitas folhinhas a voar, com o vento a sussurrar,',
        'A primavera está a chegar.',
      ],
      [
        'As pétalas, se espalham no ar,',
        'Parecem estar cantando,',
        'Um som musical a entoar,',
        'Para os pássaros ali pousando,',
        'As abelhinhas a descansar,',
        'E as joaninhas felizes passeando.',
      ],
    ],
  },
  {
    slug: 'o-sol',
    title: 'O sol',
    publishedAt: '1995-04-20',
    collection: 1,
    theme: 'Natureza',
    icon: 'sun',
    scene: 'sol',
    stanzas: [
      [
        'O sol apareceu bem cedinho,',
        'No telhado passou a colorir,',
        'Iluminou as folhas e galhos do ninho,',
        'Tão brilhante até parece sorrir.',
      ],
      [
        'Lançou seu brilho na paisagem,',
        'Realçando o trigal dourado.',
        'Refletindo no lago a sua imagem,',
        'Colorindo um tronco encalhado.',
      ],
      [
        'Vejam o sol com carinho,',
        'Pelas janelas a se infiltrar.',
        'Iluminando muitos cantinhos',
        'Enquanto a Terra está a girar.',
      ],
      [
        'O sol está muito quente!',
        'As flores desabrochando estão,',
        'As abelhinhas, tão eficientes…',
        'Trabalham muito na polinização.',
      ],
      [
        'O sol depois do entardecer,',
        'Vai para casa descansar,',
        'Ele foi feito para aquecer,',
        'A Terra, a Lua e o mar.',
      ],
      [
        'Amanhã bem cedinho,',
        'Brilha o sol nascente.',
        'Ilumina todo o caminho,',
        'Plantas e animais aguardam este presente!',
      ],
    ],
  },
  {
    slug: 'nao-sejas-egoista',
    title: 'Não sejas egoísta',
    publishedAt: '1984-02',
    collection: 1,
    theme: 'Memória',
    icon: 'smile',
    scene: 'pomar',
    stanzas: [
      [
        'Tu egoísta quer esconder,',
        'O teu sonho dourado,',
        'Deixando tudo se perder',
        'Como um frio desanimado.',
      ],
      [
        'E agora desafia a vida,',
        'Com força, sem paciência',
        'E quando não tem saída,',
        'Roga a Deus com insistência.',
      ],
      [
        'Tu já fostes meigo e sereno…',
        'Como um doce e lindo pombinho,',
        'Nos braços da mãe, pequenino…',
        'Dormia em paz bem mansinho.',
      ],
      [
        'Agora depois de crescido',
        'Cheio de graça e vontade',
        'Esqueceu o berço querido,',
        'Esqueceu toda a bondade.',
      ],
      [
        'Precisa parar para ver,',
        'Lindos pássaros no florestal.',
        'E no pomar também colher',
        'Os belos frutos no laranjal.',
      ],
      [
        'Não deixe de olhar as flores',
        'Que brotam com muito vigor',
        'São ternas, são multicores…',
        'Maravilhas de Deus com amor.',
      ],
    ],
  },
  {
    slug: 'os-pardais',
    title: 'Os pardais',
    publishedAt: '1995-02',
    collection: 1,
    theme: 'Pássaros',
    icon: 'feather',
    scene: 'pardais',
    stanzas: [
      [
        'Os pardais estão em festa',
        'No pé da jabuticabeira,',
        'Pulam de lá para cá,',
        'Tudo é uma brincadeira.',
      ],
      [
        'Entoam uma canção,',
        'Parecem que estão dizendo',
        'Que alegria, que emoção!',
        'A noite está aparecendo.',
      ],
      [
        'Nos galhos se empoleirando,',
        'Estão se espreguiçando,',
        'Também ficam se bicando,',
        'Com carinho, apenas estão brincando.',
      ],
      [
        'Esses meigos pardais calmos,',
        'Vieram de Portugal em navios,',
        'Muitos dias, em mares bravos!',
        'Enfrentaram muitos ventos frios.',
      ],
      [
        'À noite adormecem tão serenos…',
        'De manhã acordam no inverno',
        'Tão alegres, felizes e plenos',
        'Com suas vestes, nobres ternos.',
      ],
    ],
  },
  {
    slug: 'dia-de-chuva',
    title: 'Dia de chuva',
    publishedAt: '1995-02-17',
    collection: 1,
    theme: 'Natureza',
    icon: 'chuva',
    scene: 'chuva',
    stanzas: [
      [
        'Os pingos da chuva deslizam…',
        'Pelas árvores como briza,',
        'Pelos campos e cascátas,',
        'Pelas trilhas das matas.',
      ],
      [
        'Também cai lá no chão.',
        'Acabando com o torrão,',
        'Virando num lamaçal',
        'a quadra do arrozal.',
      ],
      [
        'Os animais no campo',
        'Parecem um espanto…',
        'Correm, relincham e estão a saltar',
        'Parecem a chuva saudar…',
      ],
      [
        'Correm, por todos os lados,',
        'Agradecendo a chuva e os prados.',
        'Os pássaros, quanta alegria!',
        'Que das tocas da terra fria.',
      ],
      [
        'Caçam as formigas de asas',
        'que saem de suas casas.',
        'Os pássaros ficam em euforia…',
        'Até chegar o fim do dia.',
      ],
    ],
  },
  {
    slug: 'a-natureza',
    title: 'A natureza',
    publishedAt: '1996',
    collection: 1,
    theme: 'Natureza',
    icon: 'leaf',
    scene: 'natureza',
    stanzas: [
      [
        'Observe a natureza,',
        'E veja o valor que tem,',
        'Foi Deus que fez tal beleza',
        'Pensando em nosso bem.',
      ],
      [
        'Olhe os pássaros voando,',
        'Cheios de graça e alegrias.',
        'Fazem seus ninhos cantando,',
        'Acordam cedinho todos os dias.',
      ],
      [
        'Tem peixes nos pantanais…',
        'Tem lindas e nobres garças,',
        'Patos e muitos outros animais,',
        'De muitos tipos e raças.',
      ],
      [
        'Nas matas, tem o lindo pavão,',
        'O colibri, o macaco, e o jabuti,',
        'O sabiá, o tucano e o gavião,',
        'O tigre, a onça e o juriti.',
      ],
      [
        'Os peixes enchem os mares,',
        'Os rios, lagos e lagoas.',
        'Também vivem em outros lugares.',
        'E as suas vidas, parecem boas…',
      ],
      [
        'Nas florestas, tem belas árvores,',
        'O cedro, o angico e o jamelão.',
        'Nos jardins, perfumadas flores…',
        'A rosa, o cravo e a boca-de-leão.',
      ],
      [
        'Nesta vasta e viva natureza,',
        'Ainda tem muitas riquezas,',
        'Que aqui eu não escrevi,',
        'Mas eu tenho certeza.',
      ],
    ],
  },
  {
    slug: 'cedinho-no-sitio',
    title: 'Cedinho no sítio',
    collection: 1,
    theme: 'Casa',
    icon: 'sun',
    scene: 'sitio',
    stanzas: [
      [
        'A galinha com os pintinhos,',
        'Pelas gramas saem ciscando,',
        'Vão comendo os insetinhos,',
        'Alegres juntos passeando.',
      ],
      [
        'O gado, lá no potreiro…',
        'Ficam um pouco se espreguiçando',
        'A vaca dá de mamar ao terneiro',
        'E o cavalo está pastando.',
      ],
      [
        'As lindas gotas de orvalho,',
        'Vão logo se espalhando,',
        'E as folhas dos galhos,',
        'Vão depressa se enxugando,',
        'Porque o sol já está chegando.',
      ],
      [
        'As flores vão se abrindo',
        'O sol já está a brilhar',
        'O dia está tão lindo',
        'Os pássaros estão a cantar…',
      ],
      [
        'Cedinho no sítio vou acordar,',
        'Tomar um café bem gostoso!',
        'Sair ao campo a caminhar',
        'Colher os frutos saborosos,',
        'Nas sombras das árvores descansar.',
      ],
    ],
  },

  {
    slug: 'a-noite',
    title: 'A Noite',
    publishedAt: '2022-07-10',
    collection: 2,
    theme: 'Natureza',
    icon: 'moon',
    scene: 'noite',
    stanzas: [
      [
        'A noite já está chegando,',
        'Devagar vai se aproximando,',
        'Com um vestido muito lindo,',
        'E com o véu, tudo vai colorindo.',
      ],
      [
        'Chegou com um meigo olhar,',
        'Com o sereno ela vai desfilar,',
        'Sobre as casas da cidade,',
        'Tão culta e com doce serenidade.',
      ],
      [
        'Desceu sobre os ramos das verbenas,',
        'Colorindo as flores tão plenas,',
        'Abraçou também toda a praça…',
        'Eu vejo ela através da vidraça.',
      ],
      [
        'Lá no alto, um avião passando…',
        'Com grandes asas deslizando.',
        'Sob a noite e a lua prateada,',
        'Fazendo no espaço uma estrada.',
      ],
      [
        'A noite está calma, toda estrelada',
        'Em cada passo vai andando calada.',
        'Sobre os telhados está caindo os orvalhos',
        'E as gotas estão brilhando sobre os galhos.',
      ],
      [
        'Nesta noite tão clara estou a admirar',
        'Num instante parei para contemplar,',
        'O luar e as estrelas no infinito,',
        'No majestoso céu, gigante e tão bonito.',
      ],
    ],
  },
  {
    slug: 'as-arvores',
    title: 'As Árvores',
    publishedAt: '1997-09-30',
    collection: 2,
    theme: 'Natureza',
    icon: 'leaf',
    scene: 'arvores',
    stanzas: [
      [
        'Enquanto espero o ônibus passar',
        'Estou aqui a observar',
        'As árvores e suas folhas espalhadas',
        'Nas beiradas das calçadas.',
      ],
      [
        'Vejo uma abelhinha andando na flor',
        'Entre as pétalas perfumadas a balançar',
        'E uma joaninha desfilando a sua cor',
        'E o vento está levando o perfume para o mar.',
      ],
      [
        'As suas silhuetas são tão esguias',
        'Com as suas folhas verdes e frias',
        'Estão com o vento a balançar',
        'Até parecem uma música entoar.',
      ],
      [
        'Os pássaros já estão posando,',
        'Entre as flores e galhos enfeitando',
        'Entre as belas e muitas ramadas',
        'Colorindo assim as estradas.',
      ],
      [
        'Árvores, vejo muita saúde e beleza',
        'Só olhar para vocês, a tristeza',
        'Já está bem longe de mim,',
        'Está que alegria, no fim…',
      ],
    ],
  },
  {
    slug: 'as-cigarras',
    title: 'As Cigarras',
    publishedAt: '1996-02-02',
    collection: 2,
    theme: 'Natureza',
    icon: 'wind',
    scene: 'cigarras',
    stanzas: [
      [
        'As cigarras, tão faceiras cancioneiras,',
        'Sobre as árvores frutíferas e as quaresmeiras,',
        'Enlouquecem todo o bosque e o pomar,',
        'Com seu canto radiante viajando pelo ar.',
      ],
      [
        'As cigarras coloridas são tão brilhantes,',
        'Soltam suas vozes alegres e ecoantes,',
        'Pousam sobre os galhos e flores,',
        'O som vai refletindo sobre as cores.',
      ],
      [
        'As cigarras cantoras e sanfoneiras,',
        'com suas vozes tão festeiras,',
        'Transformam o dia lindo cativante,',
        'Com suas vozes formam uma orquestra estrondante.',
      ],
    ],
  },
  {
    slug: 'bom-lugar',
    title: 'Bom Lugar',
    publishedAt: '2023-05-10',
    collection: 2,
    theme: 'Casa',
    icon: 'leaf',
    scene: 'quintal',
    stanzas: [
      [
        'Um lugar chamado jardim,',
        'Concentração muito boa enfim,',
        'Uma planta chamada alecrim,',
        'Outra planta chamada jasmim.',
      ],
      [
        'Muitas sementes se espalham pelo chão,',
        'Estão nascendo e muito lindas são,',
        'Os pássaros gostam de passear',
        'Tão felizes estão a cantar.',
      ],
      [
        'Duas rolinhas, tão meigas pombinhas,',
        'Estão no galho a descansar, tão mansinhas.',
        'Coloquei bananas para os sanhaços,',
        'Espero que um dia me deem abraços.',
      ],
      [
        'As abelhas estão sobre as florzinhas,',
        'Colhem o doce e levam para as casinhas,',
        'Sobre as flores, não posso voar,',
        'Mas estou a todas a olhar e amar.',
      ],
    ],
  },
  {
    slug: 'manha-de-sereno',
    title: 'Manhã de Sereno',
    collection: 2,
    theme: 'Natureza',
    icon: 'sun',
    scene: 'sereno',
    stanzas: [
      [
        'O sol recém apontou,',
        'por trás da grande coxilha.',
        'O cavaleiro se levantou',
        'E segue viagem pela antiga trilha.',
      ],
      [
        'A relva está brilhando',
        'cheia de gotas de orvalho.',
        'E o sereno enfeitando',
        'O grande pé de carvalho.',
      ],
      [
        'Uma borboleta colorida',
        'Por cima da flor passeando.',
        'Passou pela margarida,',
        'E continua voando, desfilando.',
      ],
      [
        'Voando numa boa a passear',
        'Deslizando, parece uma canoa…',
        'Navegando num lago que é o ar,',
        'Fez das flores uma coroa.',
      ],
      [
        'Numa manhã de sereno,',
        'Lindas paisagens podemos ver,',
        'Lá na beirada da estrada,',
        'Muitas flores lindas a colher.',
      ],
    ],
  },
  {
    slug: 'o-dia-esta-gelado',
    title: 'O Dia Está Gelado',
    publishedAt: '2023-07-13',
    collection: 2,
    theme: 'Natureza',
    icon: 'chuva',
    scene: 'gelado',
    stanzas: [
      [
        'O dia está muito gelado,',
        'O céu está tão nublado…',
        'Os campos estão sozinhos',
        'Lá para os lados de Valinhos.',
      ],
      [
        'O pasto está tão vazio.',
        'O gado não aguentou o frio,',
        'E já foi se esconder…',
        'Na sua "casa" se aquecer.',
      ],
      [
        'Só restou as árvores tremulando,',
        'Com o vento gelado passando,',
        'Lá no céu um arco-íris brilhou…',
        'Linda obra que Deus criou.',
      ],
      [
        'É o fim do dia, todos vamos dormir,',
        'Bons sonhos ter e sorrir…',
        'Amanhã vamos um lindo dia ter',
        'O sol vai brilhar e nos aquecer.',
      ],
    ],
  },
  {
    slug: 'o-meu-jardim',
    title: 'O Meu Jardim',
    publishedAt: '2023-09-02',
    collection: 2,
    theme: 'Casa',
    icon: 'leaf',
    scene: 'jardim',
    stanzas: [
      [
        'No jardim vou passear e sonhar',
        'Vou encontrar alegria todo dia.',
        'Entre as flores coloridas quero morar,',
        'Contemplar cada flor com harmonia.',
      ],
      [
        'Uma roseira tão florida',
        'Eu plantei no meu jardim',
        'Junto às lindas flores da margarida,',
        'E dálias, lírios, cravos e jasmim.',
      ],
      [
        'Os pássaros estão todos em festa,',
        'Cantam muito sem cansar.',
        'O sabiá veio da floresta',
        'E no jardim ele quer morar.',
      ],
      [
        'Um beija-flor, quanta alegria,',
        'Todos os dias vem passear.',
        'E o bem-te-vi com a sua cantoria',
        'No jardim ele quer discursar.',
      ],
      [
        'Todos os dias quando estou chegando,',
        'Tem muitos olhinhos a me olhar.',
        'Nos galhos os emplumados, estão todos vigiando.',
        'E o vento entre as flores perfume a espalhar.',
      ],
    ],
  },
  {
    slug: 'a-mima',
    title: 'A Mima',
    publishedAt: '2012-07',
    collection: 2,
    theme: 'Memória',
    icon: 'smile',
    scene: 'mima',
    stanzas: [
      [
        'Uma gata tão dengosa,',
        'Aqui em casa apareceu,',
        'Não sei se foi abandonada,',
        'Ou se alguém a perdeu.',
      ],
      [
        'No começo era tão meiga,',
        'Mas agora é tão bravinha,',
        'Não dá pra contrariá-la,',
        'Que ela mostra já suas garrinhas.',
      ],
      [
        'Todos os dias tão charmosa,',
        'A escada quer subir,',
        'Mia a Mima tão mimosa,',
        'E na cama quer dormir.',
      ],
      [
        'Mas não adianta insistir,',
        'Ela ganhou uma casinha,',
        'Água boa e bem fresquinha,',
        'E ração que é bem carinha,',
      ],
      [
        'Foi lá no veterinário,',
        'E a vacina ela tomou,',
        'Pela esperteza e beleza,',
        'Muitos elogios ela ganhou.',
      ],
    ],
  },
  {
    slug: 'passarinhos',
    title: 'Passarinhos',
    publishedAt: '2022-08-05',
    collection: 2,
    theme: 'Pássaros',
    icon: 'bird',
    scene: 'passarinhos',
    stanzas: [
      [
        'O canarinho fez seu ninho',
        'Na varanda lá de casa.',
        'Quando chego no jardim',
        'Ouço o seu canto sem fim.',
      ],
      [
        'Uma dia estava chegando,',
        'E ouvi um certo coro',
        'O sabiá estava cantando,',
        'Veio lá do alto do morro.',
      ],
      [
        'Os pássaros fazem uma festa,',
        'Cantam o sabiá e o bem-te-vi,',
        'Eles fazem uma orquestra,',
        'Todos vem cantar aqui.',
      ],
      [
        'Os passarinhos com alegria,',
        'Cantam muito, sem parar.',
        'E assim é todo o dia.',
        'Cantam muito sem cansar.',
      ],
    ],
  },
  {
    slug: 'romeu',
    title: 'Romeu',
    publishedAt: '2023-08-22',
    collection: 2,
    theme: 'Memória',
    icon: 'smile',
    scene: 'romeu',
    stanzas: [
      [
        'Romeu, Romeu. Onde você está?',
        'Quero muito te encontrar!',
        'Aonde você se escondeu?',
        'Óh meu querido Romeu!',
      ],
      [
        'Eu sou uma gata, muito bela…',
        'Olhe aqui para a janela.',
        'Não me deixam ir na rua.',
        'Ainda bem que posso ver a lua.',
      ],
      [
        'Eu vou ficar aqui olhando,',
        'Até que eu possa te ver chegando,',
        'Vou correr para te abraçar,',
        'E no jardim vamos passear.',
      ],
      [
        'A lua já está indo embora,',
        'Romeu, Romeu. Gatinho não demora,',
        'O vento passou com ciúme.',
        'Queria roubar o meu perfume.',
      ],
    ],
  },
  {
    slug: 'uma-escada',
    title: 'Uma Escada',
    publishedAt: '2023-08-20',
    collection: 2,
    theme: 'Casa',
    icon: 'janela',
    scene: 'escada',
    stanzas: [
      [
        'Uma escada toda enfeitada,',
        'Foi planejada para subir,',
        'Degrau por degrau o vento sentir.',
        'E o calor aos poucos sumir.',
      ],
      [
        'Quando chega bem lá no alto,',
        'A vista é linda: vejo os carros no asfalto.',
        'Os longos caminhos seguir,',
        'Até os raios do sol sentir.',
      ],
      [
        'A lua surgiu no espaço,',
        'A noite estendeu os braços,',
        'Uma escada surgiu no ar,',
        'Vamos sorrir e sonhar.',
      ],
      [
        'Um degrau para dormir,',
        'Até amanhã o sol surgir,',
        'Muitos trabalhos vamos ter.',
        'E pacientes sempre queremos ser.',
      ],
    ],
  },
  {
    slug: 'a-lua',
    title: 'A Lua',
    publishedAt: '2026-02-26',
    collection: 2,
    theme: 'Natureza',
    icon: 'moon',
    scene: 'lua',
    stanzas: [
      [
        'A lua brilha no horizonte',
        'Está surgindo clareando lá na ponte',
        'A lua cheia cor de prata',
        'Está subindo atrás da mata.',
      ],
      [
        'Vou ficar aqui olhando',
        'Ela vai lá desfilando',
        'Até lá no imenso mar',
        'A lua enfeitando todo lugar.',
      ],
      [
        'Eu fico aqui a sonhar',
        'Com a lua no alto a passear',
        'No infinito a sua beleza refletindo',
        'Até amanhã quando o sol nascer sorrindo.',
      ],
      [
        'A lua entre as nuvens vai sumir',
        'Atrás das colinas vai dormir',
        'Se cobrir com um véu dourado',
        'Que o sol deu emprestado.',
      ],
    ],
  },
  {
    slug: 'os-pardaizinhos',
    title: 'Os pardaizinhos',
    publishedAt: '2026-05-28',
    collection: 2,
    theme: 'Pássaros',
    icon: 'feather',
    scene: 'pardaizinhos',
    stanzas: [
      [
        'Os pardaizinhos são tão lindinhos',
        'Estão aqui nos galhos, espertinhos',
        'Vieram cedo me acordar',
        'E já começaram a cantar.',
      ],
      [
        'O seu cantar, já estou escutando',
        'Até nos telhados, já estão pulando',
        'Estão bem animados, posso ouvir',
        'Me deixam alegre, me fazem sorrir.',
      ],
      [
        'Os pardaizinhos são bons caçadores',
        'São bem espertos e bons cantores',
        'Caçam as formigas de asas',
        'Que querem invadir nossas casas.',
      ],
      [
        'Agradeça a Deus, sempre que lembrar',
        'Por ele nos dar muito o que apreciar',
        'Os pardais e a riqueza da natureza',
        'Boas criaturas com muita beleza.',
      ],
    ],
  },
]

/**
 * Shape is enforced at compile time by the `Poem` annotation above. Value-level
 * rules (slug pattern, reduced-ISO date, length budgets, theme/icon/scene
 * membership) are enforced against `poemCollectionSchema` in two hard gates: the
 * unit suite, and `scripts/prerender.ts`, which refuses to emit HTML for a
 * malformed collection. Zod deliberately never reaches the browser — see
 * shared-types/src/config.ts.
 */
export const POEMS: readonly Poem[] = Object.freeze(SOURCE_POEMS)

/**
 * Slug -> poem index. Built once; lookups are O(1).
 * A linear `Array.prototype.find` per request would be O(n) per route resolution
 * and is explicitly disallowed by rules/architecture.md.
 */
const POEM_INDEX: ReadonlyMap<string, Poem> = new Map(POEMS.map((poem) => [poem.slug, poem]))

/**
 * Slug -> ordinal in authored order.
 *
 * Exists so prev/next on the poem route is a Map probe rather than the design
 * prototype's `POEMS.indexOf(...)`, which is an O(n) scan re-run on every render.
 */
export const POEM_POSITION: ReadonlyMap<string, number> = new Map(
  POEMS.map((poem, index) => [poem.slug, index]),
)

const NO_POEMS: readonly Poem[] = Object.freeze([])

/** Single pass, O(n). Never `filter` inside `map` — that is the O(n²) shape the rules ban. */
function bucketByCollection(): ReadonlyMap<Collection['id'], readonly Poem[]> {
  const buckets = new Map<Collection['id'], Poem[]>()

  for (const poem of POEMS) {
    const bucket = buckets.get(poem.collection)
    if (bucket === undefined) {
      buckets.set(poem.collection, [poem])
    } else {
      bucket.push(poem)
    }
  }

  return new Map([...buckets].map(([id, poems]) => [id, Object.freeze(poems)]))
}

/** Precomputed at module load: one pass over 23 poems, never repeated per call. */
const POEMS_BY_COLLECTION = bucketByCollection()

/** O(1). Returns `undefined` for any slug that is not published. */
export function findPoemBySlug(slug: string): Poem | undefined {
  return POEM_INDEX.get(slug)
}

/** O(1). Authored order: collection I, then collection II. */
export function listPoems(): readonly Poem[] {
  return POEMS
}

/**
 * O(1) — the same frozen array identity on every call, so a consumer may compare
 * by reference and skip work. Never filters.
 */
export function listPoemsByCollection(id: Collection['id']): readonly Poem[] {
  return POEMS_BY_COLLECTION.get(id) ?? NO_POEMS
}
